import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../../components/Navigation';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Resources() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showVideoPopup, setShowVideoPopup] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<any>(null);
  const [videoGuides, setVideoGuides] = useState<any[]>([]);
  const [newsArticles, setNewsArticles] = useState<any[]>([]);
  const [heroSection, setHeroSection] = useState<any>(null);
  const [resourceSections, setResourceSections] = useState<any[]>([]);
  const [ctaSection, setCtaSection] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      // Fetch hero section
      const { data: heroData } = await supabase
        .from('hero_sections')
        .select('*')
        .eq('page', 'resources')
        .eq('is_active', true)
        .single();

      if (heroData) setHeroSection(heroData);

      // Fetch resource sections
      const { data: sectionsData } = await supabase
        .from('resource_sections')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (sectionsData) setResourceSections(sectionsData);

      // Fetch video guides
      const { data: videosData } = await supabase
        .from('video_guides')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (videosData) setVideoGuides(videosData);

      // Fetch news articles
      const { data: newsData } = await supabase
        .from('news_articles')
        .select('*')
        .eq('status', 'Published')
        .order('published_at', { ascending: false });

      if (newsData) setNewsArticles(newsData);

      // Fetch CTA section
      const { data: ctaData } = await supabase
        .from('cta_sections')
        .select('*')
        .eq('page', 'resources')
        .eq('is_active', true)
        .single();

      if (ctaData) setCtaSection(ctaData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching content:', error);
      setLoading(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Resources - Documentation, News & Video Guides",
    "description": "Access comprehensive resources including documentation, latest news, announcements, and video tutorials for our enterprise platforms.",
    "url": `${import.meta.env.VITE_SITE_URL}/resources`
  };

  const filteredNews = newsArticles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['all', ...Array.from(new Set(newsArticles.map(a => a.category)))];

  const openVideoPopup = (video: any) => {
    setSelectedVideo(video);
    setShowVideoPopup(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Resources - Documentation, News & Video Guides | Allgo Digital"
        description="Access comprehensive resources including documentation, latest news, announcements, and video tutorials for our enterprise platforms."
        keywords="resources, documentation, news, video guides, tutorials, help docs"
        canonical="/resources"
        structuredData={structuredData}
      />

      {/* Navigation */}
      <Navigation />

      {/* Back Button */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-teal-600 to-emerald-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            {heroSection?.title || 'Resources & Learning Center'}
          </h1>
          <p className="text-lg sm:text-xl text-teal-100 max-w-3xl mx-auto">
            {heroSection?.subtitle || 'Everything you need to succeed with our platforms - from documentation and tutorials to the latest news and updates.'}
          </p>
        </div>
      </section>

      {/* Quick Access Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {resourceSections.length > 0 ? resourceSections.map((section) => (
              <div key={section.id} className="bg-white rounded-xl shadow-lg p-8 text-center">
                <div className={`w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6`} style={{ backgroundColor: `${section.icon_color || '#3B82F6'}20` }}>
                  <i className={`${section.icon || 'ri-book-open-line'} text-2xl`} style={{ color: section.icon_color || '#3B82F6' }}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h3>
                <p className="text-gray-600 mb-6">{section.description}</p>
                <a 
                  href={section.link_url || '#'} 
                  target={section.link_url?.startsWith('http') ? '_blank' : '_self'}
                  rel={section.link_url?.startsWith('http') ? 'noopener noreferrer' : ''}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block cursor-pointer"
                >
                  {section.link_text || 'Learn More'}
                </a>
              </div>
            )) : (
              <>
                {/* Default sections fallback */}
                <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                    <i className="ri-book-open-line text-2xl text-blue-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Help Documents</h3>
                  <p className="text-gray-600 mb-6">
                    Comprehensive documentation, API references, and troubleshooting guides for all our platforms.
                  </p>
                  <a 
                    href="https://www.notion.so/Leansight-Product-Overview-2b56d924ca6980f89c12ee54798fb456?source=copy_link" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block cursor-pointer"
                  >
                    View Documentation
                  </a>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                    <i className="ri-play-circle-line text-2xl text-blue-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Video Guides</h3>
                  <p className="text-gray-600 mb-6">
                    Step-by-step video tutorials and webinars to help you master our platform features.
                  </p>
                  <a 
                    href="https://www.youtube.com/@AllgoTechnologies"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block cursor-pointer"
                  >
                    Watch Video Tutorials
                  </a>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-6">
                    <i className="ri-newspaper-line text-2xl text-blue-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">News & Updates</h3>
                  <p className="text-gray-600 mb-6">
                    Stay informed with the latest product updates, company news, and industry insights.
                  </p>
                  <a 
                    href="#news-section"
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block cursor-pointer"
                  >
                    Read Latest News
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Video Guides Section */}
      <section id="video-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Video Tutorials
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Learn from our comprehensive video library covering all aspects of our platforms.
            </p>
          </div>

          {videoGuides.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {videoGuides.map((video) => (
                <div key={video.id} className="bg-gray-50 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow cursor-pointer" onClick={() => openVideoPopup(video)}>
                  <div className="relative">
                    <img 
                      src={video.thumbnail_url || `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28video.title%29%7D&width=300&height=200&seq=${video.id}&orientation=landscape`}
                      alt={video.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <i className="ri-play-circle-fill text-4xl text-white"></i>
                    </div>
                    {video.duration && (
                      <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm">
                        {video.duration}
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      {video.difficulty && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          video.difficulty === 'Beginner' ? 'bg-green-100 text-green-700' :
                          video.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {video.difficulty}
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {video.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {video.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <i className="ri-video-line text-4xl text-gray-300 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No video guides available</h3>
              <p className="text-gray-500">Check back soon for new tutorials</p>
            </div>
          )}
        </div>
      </section>

      {/* News & Announcements Section */}
      <section id="news-section" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              News & Announcements
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay up to date with the latest developments, product updates, and company news.
            </p>
          </div>

          {/* Search and Filter */}
          <div className="mb-8 flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search news articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
            </div>
            <div className="md:w-64">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* News List */}
          {filteredNews.length > 0 ? (
            <div className="space-y-6">
              {filteredNews.map((article) => (
                <div key={article.id} className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col md:flex-row gap-6">
                    {article.featured_image && (
                      <div className="md:w-1/4">
                        <img 
                          src={article.featured_image}
                          alt={article.title}
                          className="w-full h-48 md:h-32 object-cover rounded-lg"
                        />
                      </div>
                    )}
                    <div className={article.featured_image ? 'md:w-3/4' : 'w-full'}>
                      <div className="flex items-center gap-4 mb-3">
                        <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                          {article.category}
                        </span>
                        <span className="text-gray-500 text-sm">{new Date(article.published_at).toLocaleDateString()}</span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3 hover:text-blue-600 cursor-pointer">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">By {article.author}</span>
                        <button className="text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
                          Read More <i className="ri-arrow-right-line ml-1"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No articles found</h3>
              <p className="text-gray-500">Try adjusting your search terms or category filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* Video Popup */}
      {showVideoPopup && selectedVideo && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedVideo.title}</h3>
                <p className="text-gray-600">{selectedVideo.description}</p>
              </div>
              <button 
                onClick={() => setShowVideoPopup(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            
            <div className="mb-6">
              <img 
                src={selectedVideo.thumbnail_url || `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28selectedVideo.title%29%7D&width=600&height=400&seq=${selectedVideo.id}&orientation=landscape`}
                alt={selectedVideo.title}
                className="w-full h-64 object-cover rounded-lg mb-4"
              />
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                {selectedVideo.duration && (
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {selectedVideo.duration}
                  </span>
                )}
                {selectedVideo.difficulty && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    selectedVideo.difficulty === 'Beginner' ? 'bg-green-100 text-green-700' :
                    selectedVideo.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {selectedVideo.difficulty}
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-4 justify-center">
              <a 
                href={selectedVideo.youtube_url}
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors cursor-pointer flex items-center gap-2"
              >
                <i className="ri-youtube-line"></i>
                Watch on YouTube
              </a>
              <button 
                onClick={() => setShowVideoPopup(false)}
                className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-400 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {ctaSection?.title || 'Need Additional Support?'}
          </h2>
          <p className="text-lg sm:text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
            {ctaSection?.subtitle || 'Our support team is here to help you succeed with our platforms. Get personalized assistance and expert guidance.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={ctaSection?.primary_cta_link || '/contact'} className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              {ctaSection?.primary_cta_text || 'Schedule Demo'}
            </Link>
            <Link to={ctaSection?.secondary_cta_link || '/support'} className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
              {ctaSection?.secondary_cta_text || 'Contact Support'}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/feaac7b19917aec559683ef9ad3d4cd6/f01248d0a1f191b5d9d27b9480b2a552.jpeg" 
                alt="Allgo Digital Logo" 
                className="h-10 w-auto mb-4"
              />
              <p className="text-gray-400">
                Transforming enterprise software development with integrated low-code, data flow, and BI solutions.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Platforms</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Codee Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Fusion Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">BI Platform</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/resources" className="hover:text-white cursor-pointer">Resources</Link></li>
                <li><Link to="/features" className="hover:text-white cursor-pointer">Features</Link></li>
                <li><Link to="/partners" className="hover:text-white cursor-pointer">Partner Program</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>info@allgo-digital.com</li>
                <li>+60 3-7931 8888</li>
                <li>Enterprise Solutions</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Allgo Digital Sdn Bhd. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
